import { omit, pick, union, without } from 'es-toolkit';
import * as React from 'react';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import InfoIcon from '@mui/icons-material/Info';
import {
  AppBar,
  Box,
  Button,
  Dialog,
  Grid,
  IconButton,
  List,
  ListItemButton,
  ListItemSecondaryAction,
  ListItemText,
  Paper,
  Slide,
  Snackbar,
  Toolbar,
  Typography,
} from '@mui/material';
import { TransitionProps } from '@mui/material/transitions';

import { naturalSort, pickBy } from '@paragon/utilities';

import { UploadedFile } from '../files/UploadedFile';
import { ComponentFileCollection, MiscellaneousFileCollection } from '../types';

const hiddenMiscFiles = ['dwg', 'pic', 'spl', 'xtd', 'eng', 'pe'];

function isVisible(file: UploadedFile) {
  if (file.filetype == null) {
    return true;
  }

  return !hiddenMiscFiles.includes(file.filetype.toLowerCase());
}

export interface FileManagerResult {
  readonly componentDesigns: ComponentFileCollection;
  readonly miscellaneousFiles: MiscellaneousFileCollection;
  readonly metaPdfs: MiscellaneousFileCollection;
}

interface Props {
  readonly requireComponentsOrOtherPdfs: boolean;
  readonly showOther: boolean;
  readonly componentDesigns: ComponentFileCollection;
  readonly miscellaneousFiles: MiscellaneousFileCollection;

  readonly onCancel: () => void;
  readonly onSubmit: (files: FileManagerResult) => void;
}

interface FileManagerState {
  deletedComponents: string[];
  deletedMiscellaneous: string[];
  movedMiscellaneous: string[];
  selected: string | null;
  filetype?: string | null;
  open: boolean;
  focus: boolean;
  toggle: boolean;
}

const Transition = React.forwardRef(
  (props: TransitionProps & { children: React.ReactElement }, ref: React.Ref<unknown>) => (
    <Slide direction="up" ref={ref} {...props} />
  ),
);

export class FileManager extends React.Component<Props, FileManagerState> {
  state: FileManagerState = {
    deletedComponents: [],
    deletedMiscellaneous: [],
    movedMiscellaneous: [],
    selected: null,
    open: true,
    focus: true,
    toggle: false,
  };

  onSelectFile(key: string) {
    this.setState({
      selected: key,
    });
  }

  get componentDesigns(): ComponentFileCollection {
    return omit(this.props.componentDesigns, this.state.deletedComponents);
  }

  get otherPdfsToSeal(): MiscellaneousFileCollection {
    const miscKeys = without(this.state.movedMiscellaneous, ...this.state.deletedMiscellaneous);
    return pick(this.props.miscellaneousFiles, miscKeys);
  }

  get miscellaneousFiles(): MiscellaneousFileCollection {
    const keysToOmit = union(this.state.movedMiscellaneous, this.state.deletedMiscellaneous);
    return omit(this.props.miscellaneousFiles, keysToOmit);
  }

  get displayedMiscellaneousFiles(): MiscellaneousFileCollection {
    return pickBy(this.miscellaneousFiles, isVisible);
  }

  handleClickOpen = () => {
    this.setState({ open: true });
  };

  handleClose = () => {
    this.setState({ open: false });
  };

  toggleSnack = () => {
    this.setState({ toggle: true });
  };

  toggleSnackClose = (event: any, reason: any) => {
    if (reason === 'clickaway') {
      return;
    }

    this.setState({ toggle: false });
  };

  moveSelectedToOther = () => {
    if (this.state.selected == null) {
      return;
    }

    const selectedFile = this.props.miscellaneousFiles[this.state.selected];
    if (selectedFile != null && selectedFile.filetype && selectedFile.filetype.toLowerCase() === 'pdf') {
      this.setState((state) => ({
        movedMiscellaneous: union(state.movedMiscellaneous, [state.selected!]),
      }));
    }
  };

  moveSelectedToMiscellaneous = () => {
    if (this.state.selected == null) {
      return;
    }

    this.setState((state) => ({
      movedMiscellaneous: without(state.movedMiscellaneous, state.selected!),
    }));
  };

  moveAllToOther = () => {
    const miscellaneousFilesNameArray = Object.entries(this.props.miscellaneousFiles)
      .filter(([_, file]) => file.filetype && file.filetype.toLowerCase() === 'pdf')
      .map(([fileName, _]) => `${fileName.toLowerCase()}`);
    this.setState((state) => ({
      movedMiscellaneous: union(state.movedMiscellaneous, miscellaneousFilesNameArray),
    }));
  };

  onSubmit = () => {
    if (this.props.requireComponentsOrOtherPdfs) {
      const areComponentDesigns = Object.keys(this.componentDesigns).length > 0;
      const areOtherPdfsToSeal = Object.keys(this.otherPdfsToSeal).length > 0;
      const areMiscellaneousFiles = Object.keys(this.miscellaneousFiles).length > 0;
      if (!areComponentDesigns && !areOtherPdfsToSeal && !areMiscellaneousFiles) {
        alert('Please upload a .ZIP with files.');
        return;
      }
    }

    this.props.onSubmit({
      componentDesigns: this.componentDesigns,
      miscellaneousFiles: this.miscellaneousFiles,
      metaPdfs: this.otherPdfsToSeal,
    });
  };

  deleteComponentWithKey(key: string) {
    this.setState((state) => ({
      deletedComponents: state.deletedComponents.concat(key),
    }));
  }

  deleteMiscellaneousWithKey(key: string) {
    this.setState((state) => ({
      deletedMiscellaneous: state.deletedMiscellaneous.concat(key),
    }));
  }

  render() {
    const componentCount = Object.entries(this.componentDesigns).length;

    const components = Object.keys(this.componentDesigns)
      .toSorted(naturalSort)
      .map((componentName) => (
        <ListItemButton key={componentName}>
          <ListItemText primary={componentName} />
          <ListItemSecondaryAction>
            <IconButton aria-label="Delete" onClick={() => this.deleteComponentWithKey(componentName)}>
              <DeleteIcon />
            </IconButton>
          </ListItemSecondaryAction>
        </ListItemButton>
      ));

    const otherCount = Object.entries(this.otherPdfsToSeal).length;

    const other = Object.entries(this.otherPdfsToSeal).map(([key, file]) => (
      <ListItemButton
        key={key}
        onClick={() => this.onSelectFile(key)}
        onDoubleClick={this.moveSelectedToMiscellaneous}
        sx={key === this.state.selected ? { bgcolor: 'selectedList.main' } : null}
      >
        <ListItemText primary={file.filename} />
        <ListItemSecondaryAction>
          <IconButton aria-label="Delete" onClick={() => this.deleteMiscellaneousWithKey(key)}>
            <DeleteIcon />
          </IconButton>
        </ListItemSecondaryAction>
      </ListItemButton>
    ));

    const miscellaneousCount = Object.entries(this.displayedMiscellaneousFiles).length;

    const miscellaneous = Object.entries(this.displayedMiscellaneousFiles).map(([key, file]) => (
      <ListItemButton
        key={key}
        onClick={() => this.onSelectFile(key)}
        onDoubleClick={this.moveSelectedToOther}
        sx={key === this.state.selected ? { bgcolor: 'selectedList.main' } : null}
      >
        <ListItemText primary={file.filename} />
        <ListItemSecondaryAction sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography>{file.filetype ? file.filetype.toUpperCase() : undefined}</Typography>
          <IconButton aria-label="Delete" onClick={() => this.deleteMiscellaneousWithKey(key)}>
            <DeleteIcon />
          </IconButton>
        </ListItemSecondaryAction>
      </ListItemButton>
    ));

    const pdfIsSelected =
      this.state.selected &&
      this.props.miscellaneousFiles[this.state.selected] &&
      this.props.miscellaneousFiles[this.state.selected].filetype!.toLowerCase() === 'pdf';

    const canMakeMetaPdf =
      this.state.selected && pdfIsSelected && !this.state.movedMiscellaneous.includes(this.state.selected);

    const canUnmakeMetaPdf =
      this.state.selected && pdfIsSelected && this.state.movedMiscellaneous.includes(this.state.selected);

    const componentColumnTitle = this.props.showOther ? 'Components to Seal' : 'Components to Add';

    const otherColumn = this.props.showOther ? (
      <>
        <Grid size="grow">
          <Typography variant="h6" sx={{ mt: 4, mx: 0, mb: 2 }}>
            Other PDFs to Seal ({otherCount})
          </Typography>
          <Paper
            sx={{
              px: {
                xs: 2,
                sm: 3,
              },
              py: 2,
              bgcolor: 'backContrast.main',
            }}
          >
            <List
              sx={{
                height: 500,
                maxHeight: 900,
                overflowY: 'hidden',
                '&:hover': {
                  overflowY: 'auto',
                },
                '&::-webkit-scrollbar': {
                  display: 'none',
                },
                scrollbarWidth: 'none',
              }}
            >
              {other}
            </List>
          </Paper>
        </Grid>

        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
          <IconButton
            aria-label="left"
            disabled={!canMakeMetaPdf}
            onClick={this.moveSelectedToOther}
            className="leftArrowFileManager"
          >
            <ArrowBackIcon />
          </IconButton>
          <IconButton
            aria-label="right"
            disabled={!canUnmakeMetaPdf}
            onClick={this.moveSelectedToMiscellaneous}
            className="rightArrowFileManager"
          >
            <ArrowForwardIcon />
          </IconButton>
          <IconButton
            aria-label="double-left"
            onClick={this.moveAllToOther}
            className="doubleLeftArrowsFileManager"
            title="Move All"
          >
            <DoubleArrowIcon sx={{ transform: 'rotate(180deg)' }} />
          </IconButton>
        </Box>
      </>
    ) : null;

    return (
      <Dialog
        fullScreen
        disableRestoreFocus={this.state.focus}
        open={this.state.open}
        onClose={this.handleClose}
        slots={{ transition: Transition }}
      >
        <AppBar
          sx={{
            position: 'relative',
            backgroundColor: '#505050',
            borderBottom: 'solid 3px #f4d166',
            color: '#fff',
          }}
        >
          <Toolbar
            disableGutters
            sx={{
              pl: {
                xs: 2,
                sm: 3,
              },
            }}
          >
            <Typography variant="h6" sx={{ color: 'inherit', flex: 1 }}>
              File Manager
            </Typography>
            <Button sx={{ m: 1, mr: 2 }} variant="contained" onClick={this.onSubmit}>
              Confirm
            </Button>
            <IconButton
              color="inherit"
              onClick={this.props.onCancel}
              aria-label="Close"
              sx={{
                height: 64,
                width: 64,
                fontSize: 28,
                bgcolor: 'paragon.main',
                borderRadius: 0,
              }}
            >
              <CloseIcon sx={{ fontSize: 28 }} />
            </IconButton>
          </Toolbar>
        </AppBar>
        <Box sx={{ flexGrow: 1, bgcolor: 'drawerDialog.main' }}>
          <Grid sx={{ width: '100%', m: 0, p: 3 }} container spacing={3}>
            <Grid size="grow">
              <Typography variant="h6" sx={{ mt: 4, mx: 0, mb: 2 }}>
                {componentColumnTitle} ({componentCount})
              </Typography>
              <Paper
                sx={{
                  px: {
                    xs: 2,
                    sm: 3,
                  },
                  py: 2,
                  bgcolor: 'backContrast.main',
                }}
              >
                <List
                  sx={{
                    height: 500,
                    maxHeight: 900,
                    overflowY: 'hidden',
                    '&:hover': {
                      overflowY: 'auto',
                    },
                    '&::-webkit-scrollbar': {
                      display: 'none',
                    },
                    scrollbarWidth: 'none',
                  }}
                >
                  {components}
                </List>
              </Paper>
            </Grid>

            {otherColumn}

            <Grid size="grow">
              <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
                <Typography variant="h6" sx={{ mt: 4, mx: 0, mb: 2 }}>
                  Miscellaneous Files ({miscellaneousCount})
                </Typography>
                <IconButton aria-label="info" onClick={this.toggleSnack} sx={{ m: 1 }}>
                  <InfoIcon />
                </IconButton>
                <Snackbar
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                  }}
                  open={this.state.toggle}
                  autoHideDuration={6000}
                  onClose={this.toggleSnackClose}
                  slotProps={{ content: { 'aria-describedby': 'message-id' } }}
                  message={<span id="message-id">Use arrows to move any PDFs that require seals</span>}
                />
              </Box>
              <Paper
                sx={{
                  px: {
                    xs: 2,
                    sm: 3,
                  },
                  py: 2,
                  bgcolor: 'backContrast.main',
                }}
              >
                <List
                  sx={{
                    height: 500,
                    maxHeight: 900,
                    overflowY: 'hidden',
                    '&:hover': {
                      overflowY: 'auto',
                    },
                    '&::-webkit-scrollbar': {
                      display: 'none',
                    },
                    scrollbarWidth: 'none',
                  }}
                  id="miscellaneousFileColumn"
                >
                  {miscellaneous}
                </List>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      </Dialog>
    );
  }
}
